import React, { useEffect, useState } from 'react';
import { db } from '../../Libs/firebase-config.mjs';
import { doc, getDoc } from 'firebase/firestore';

// Shows the main partner list (aboutpage / partnerships) together with the
// MbuziPlus partner list (mbuziplusprogram / main). Both are read live, so a
// logo changed in the backend changes here too; nothing is copied.
const DEFAULTS = [
  { name: 'Neotex', image: 'https://res.cloudinary.com/dpymwa41m/image/upload/v1760896768/neotext_qjeahl.jpg' },
  { name: 'Rafiki Microfinance Bank', image: 'https://res.cloudinary.com/dpymwa41m/image/upload/v1760896719/imfuyo_rafiki_h3l02a.png' },
  { name: 'Lead Farmer', image: 'https://res.cloudinary.com/dpymwa41m/image/upload/v1760896670/lead_tsoan9.png' },
  { name: 'Muthegi Agricultural Development Forum', image: 'https://res.cloudinary.com/dpymwa41m/image/upload/v1760896649/Muthegilogo_egnfmj.jpg' },
  { name: 'Nyeri County Government', image: 'https://res.cloudinary.com/dpymwa41m/image/upload/v1760896621/imfuyo_nyeri_kvz0ij.png' },
];

// One logo per organisation: the same partner can be spelled differently in the
// two backend lists, so these are treated as the same organisation.
const keyFor = (name) => {
  const n = name.trim().toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ');
  if (/paystack.*stripe/.test(n)) return 'paystack stripe';
  if (/dgak|dairy goats? association/.test(n)) return 'dgak';
  if (/^sbm.*(bancassurance|insurance)/.test(n)) return 'sbm bancassurance';
  if (/^sbm/.test(n)) return 'sbm';
  if (/jkuat|jomo kenyatta/.test(n)) return 'jkuat';
  if (/^nyeri/.test(n)) return 'nyeri';
  if (/^muthegi|^muthigi/.test(n)) return 'muthegi';
  return n;
};

// Clearest, highest-quality logos first (only the first six show until "See all"); unlisted names follow.
const FIRST = ['paystack stripe', 'bcg', 'glovo', 'esade', 'sbm bancassurance', 'sbm',
  'eworks', 'caisley eartag', 'ashoka', 'jkuat', 'rafiki microfinance bank', 'narok county',
  'pacific insurance brokers', 'gearbox europlacer', 'softeq', 'emc3', 'nyeri', 'dgak', 'muthegi',
  'neotex', 'lead farmer', 'true leap', 'edc', 'sbdc', 'satima', 'billy', 'neuafrik', 'charis', 'kldc'];

// Logos supplied directly (stored with the website). A backend logo with the same name wins.
const EXTRA = [
  { name: 'Paystack and Stripe', image: '/partners/paystack-stripe.png' },
  { name: 'BCG', image: '/partners/bcg.png' },
  { name: 'Glovo', image: '/partners/glovo.png' },
  { name: 'Esade', image: '/partners/esade.png' },
  { name: 'SBM Bancassurance', image: '/partners/sbm-bancassurance.png' },
  { name: 'Caisley Eartag', image: '/partners/caisley.png' },
  { name: 'eWorks', image: '/partners/eworks.png' },
];
const rank = (p) => { const i = FIRST.indexOf(keyFor(p.name)); return i < 0 ? 99 : i; };

// Logos that replace whatever the backend holds for that organisation.
const OVERRIDE = { muthegi: '/partners/muthegi.png' };

// Cloudinary logos: trim the blank border and cap the size, so every logo fills its box the same way.
const tidy = (url) => (/res\.cloudinary\.com\/[^/]+\/image\/upload\/v\d/.test(url)
  ? url.replace('/image/upload/', '/image/upload/e_trim:12/c_limit,w_700,h_350/q_auto,f_auto/')
  : url);

const CACHE_BASE = 'nx-partner-logos-v8';

const Tile = ({ p }) => {
  const [failed, setFailed] = useState(!p.image);
  return (
    <div className="logo" title={p.name} style={{ minHeight: 124 }}>
      {failed ? (
        <span>{p.name}</span>
      ) : (
        <img src={tidy(p.image)} alt={p.name} onError={() => setFailed(true)}
             className="plogo" />
      )}
    </div>
  );
};

const usePhone = () => {
  const q = '(max-width: 620px)';
  const [phone, setPhone] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const f = () => setPhone(m.matches);
    f();
    m.addEventListener ? m.addEventListener('change', f) : m.addListener(f);
    return () => { m.removeEventListener ? m.removeEventListener('change', f) : m.removeListener(f); };
  }, []);
  return phone;
};

export default function PartnerLogos({ mainOnly = false }) {
  const phone = usePhone();
  const [showAll, setShowAll] = useState(false);
  const CACHE = CACHE_BASE + (mainOnly ? '-main' : '-all');
  const [list, setList] = useState(() => {
    try { const c = JSON.parse(sessionStorage.getItem(CACHE)); if (Array.isArray(c) && c.length) return c; } catch (e) {}
    return DEFAULTS;
  });

  useEffect(() => {
    let alive = true;
    const read = async (path) => {
      try {
        const snap = await getDoc(doc(db, path[0], path[1]));
        if (!snap.exists()) return [];
        return (snap.data().partners || []).filter((p) => p && p.name)
          .map((p) => ({ name: p.name, image: p.image || '' }));
      } catch (e) { return []; }
    };
    (async () => {
      // Main partner list first, then the MbuziPlus partner list (read live, not copied)
      const [main, mbuzi] = await Promise.all([
        read(['aboutpage', 'partnerships']),
        mainOnly ? Promise.resolve([]) : read(['mbuziplusprogram', 'main']),
      ]);
      const merged = [];
      const seen = new Map();
      [...main, ...mbuzi, ...EXTRA].forEach((p) => {
        const key = keyFor(p.name);
        if (key === 'paystack') return;
        if (seen.has(key)) {
          const i = seen.get(key);
          if (!merged[i].image && p.image) merged[i].image = p.image;
        } else { seen.set(key, merged.length); merged.push({ ...p }); }
      });
      merged.forEach((p) => { const o = OVERRIDE[keyFor(p.name)]; if (o) p.image = o; });
      if (alive && merged.length) {
        setList(merged);
        try { sessionStorage.setItem(CACHE, JSON.stringify(merged)); } catch (e) {}
      }
    })();
    return () => { alive = false; };
  }, []);

  const ordered = [...list].sort((a, b) => rank(a) - rank(b));
  const all = !showAll ? ordered.slice(0, 6) : ordered;

  return (
    <>
    <div className="g5" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
      {all.map((p, i) => <Tile key={p.name + i} p={p} />)}
    </div>
    {list.length > 6 && (
      <div style={{ display: 'flex' }}>
        <button type="button" className="more-btn" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show fewer partners' : `See all ${list.length} partners`}
        </button>
      </div>
    )}
    </>
  );
}
